package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.AvatarGenerationViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.data.dto.PricingDto
import com.arena_web.data.dto.ProfileCardDto
import com.arena_web.data.dto.UserCardDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.domain.repositories.CardRepository
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.widgets.CoinIcon
import com.arena_web.ui.components.widgets.LoadingSpinner
import com.arena_web.ui.components.widgets.formatCoins
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.maxLength
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.rows
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import org.w3c.files.get

// Figma 7787:469541 — AI Avatar dialog. Same dark surface as the parent
// "Bring Your Card to Life" popup.
private const val DIALOG_BG = "#09142d"

// Dashed drop-zone — football/data/chart/line-stroke token (#0c1787) with the
// translucent blue surface used for inactive interactive surfaces elsewhere.
private const val DROPZONE_BORDER = "#0c1787"
private const val DROPZONE_BG =
    "linear-gradient(189.74deg, rgba(60, 121, 241, 0.06) 49.5%, rgba(5, 4, 12, 0.06) 105.9%)"

// Translucent blue fill shared by the prompt textarea and the outlined footer
// buttons (Figma "Event status" component).
private val INTERACTIVE_TRANSLUCENT_BG =
    AppGradients.InteractiveSecondary

// Hard caps mirrored from the Figma annotations.
private const val MAX_FILE_SIZE = 30 * 1024 * 1024 // 30MB per design
private const val MAX_PROMPT_CHARS = 300
private val ALLOWED_TYPES = setOf("image/jpeg", "image/png", "image/webp")

private data class PickedFile(
    val bytes: ByteArray,
    val name: String,
    val type: String,
    val previewUrl: String,
)

/**
 * AI-avatar generation dialog — Figma 7787:469541.
 *
 * Pipeline:
 *   1. User drops/picks a photo (jpeg/png/webp, ≤30MB) and optionally types a
 *      custom prompt up to 300 chars.
 *   2. **Generate** uploads the photo (or refines the existing attempt with
 *      just the prompt) and waits for the AI result to land in the right-hand
 *      "Generated Look" slot. The result is auto-saved as the user's avatar the
 *      moment it lands — no manual Apply step — and the dialog stays open so the
 *      user can keep refining.
 *   3. **Cancel** closes the dialog; the last saved attempt is kept.
 */
@Composable
fun AvatarGenerationModal(
    isOpen: Boolean,
    onClose: () -> Unit,
    cardRepository: CardRepository,
    /** Owned-template id (`UserCard.id`) — what the generation endpoints take. */
    cardId: String,
    /**
     * Existing main profile card, if any — its avatar drives the "current"
     * silhouette on the left card. Pass null when the user has no gallery
     * instance yet (a clean silhouette is rendered instead).
     */
    mainProfileCard: ProfileCardDto?,
    /**
     * Template visuals used for both card slots (frame, assets, text colour).
     * Comes from the owned `UserCardDto` the user picked in the previous step.
     */
    fallbackCard: UserCardDto,
    sportName: String,
    onAvatarConfirmed: () -> Unit,
    /** Editing an existing gallery card — a prompt alone (no upload) regenerates
     *  from that card's current photo. Null for the create-new-card flow. */
    editProfileCardId: String? = null,
) {
    if (!isOpen) return

    val viewModel = rememberViewModel(key = "$cardId:${editProfileCardId ?: ""}") {
        AvatarGenerationViewModel(cardRepository, cardId, editProfileCardId)
    }
    val state by viewModel.uiState.collectAsState()

    // Pull pricing + wallet from the app-level cache. First open triggers a
    // fetch; subsequent opens read the cached values instantly.
    val container = LocalAppContainer.current
    val pricing by container.pricing.collectAsState()
    val walletBalance by container.walletBalance.collectAsState()
    LaunchedEffect(Unit) {
        container.ensurePricingLoaded()
        container.ensureWalletLoaded()
    }

    val isBusy = state.isGenerating || state.isConfirming
    val hasNewAttempt = state.newAttempt != null
    // In edit mode a prompt alone can regenerate from the card's existing photo,
    // so the upload becomes optional just like a normal refine.
    val canRefineExisting = state.latestStoredAttemptId != null || editProfileCardId != null

    // Close is locked while a request is in flight (generation + the auto-save
    // confirm that follows it) so we never close mid-flight on billed work.
    val closeLocked = isBusy

    // The dialog stays open after each generation (it auto-saves to the backend
    // the moment a result lands, so the user can keep refining). We notify the
    // caller — which both refreshes its profile and closes us — only when the
    // user dismisses, and only if at least one attempt was saved this session.
    val dismiss = {
        if (!closeLocked) {
            if (state.isConfirmed) onAvatarConfirmed()
            onClose()
        }
    }

    // True when the user has less than the avatar-generation price in their
    // wallet. Until pricing/balance load we optimistically allow the action;
    // the backend will reject it as a safety net.
    fun canAfford(): Boolean {
        val price = pricing?.avatarGeneration?.toDoubleOrNull() ?: return true
        val balance = walletBalance?.toDoubleOrNull() ?: return true
        return balance >= price
    }

    // Insufficient balance → open the wallet. BuyCoinsDialog renders the mobile
    // sheet vs the desktop popup itself.
    var walletOpen by remember { mutableStateOf(false) }
    val mobile = isMobile()
    val onInsufficientBalance = { walletOpen = true }

    AvatarDialog(
        cardVisual = fallbackCard,
        sportName = sportName,
        currentAvatarUrl = mainProfileCard?.avatarUrl,
        newAvatarUrl = state.newAttempt?.generatedAvatarUrl,
        hasNewAttempt = hasNewAttempt,
        canRefineExisting = canRefineExisting,
        isGenerating = state.isGenerating,
        isConfirming = state.isConfirming,
        backendError = state.error,
        pricing = pricing,
        closeLocked = closeLocked,
        onClose = dismiss,
        onUpload = { bytes, name, type, prompt ->
            if (canAfford()) {
                viewModel.uploadPhoto(bytes, name, type, prompt)
            } else {
                onInsufficientBalance()
            }
        },
        onRefine = { prompt ->
            if (canAfford()) {
                viewModel.refine(prompt)
            } else {
                onInsufficientBalance()
            }
        },
    )

    // Insufficient balance → open the wallet. Layered above the avatar modal
    // (desktop z 1010) / sheet (mobile z 1100).
    BuyCoinsDialog(
        isOpen = walletOpen,
        onClose = { walletOpen = false },
        walletBalance = walletBalance,
        zIndex = if (mobile) 1200 else 1020,
    )
}

// =============================================================================
// Dialog shell
// =============================================================================
@Composable
private fun AvatarDialog(
    cardVisual: UserCardDto,
    sportName: String,
    currentAvatarUrl: String?,
    newAvatarUrl: String?,
    hasNewAttempt: Boolean,
    canRefineExisting: Boolean,
    isGenerating: Boolean,
    isConfirming: Boolean,
    pricing: PricingDto?,
    backendError: String?,
    closeLocked: Boolean,
    onClose: () -> Unit,
    onUpload: (ByteArray, String, String, String?) -> Unit,
    onRefine: (String) -> Unit,
) {
    val t = LocalAppStrings.current
    var pickedFile by remember { mutableStateOf<PickedFile?>(null) }
    var prompt by remember { mutableStateOf("") }
    var validationError by remember { mutableStateOf<String?>(null) }

    // Clear the file/prompt only once the request actually fires. When the
    // parent intercepts (e.g. insufficient balance → opens the top-up dialog)
    // the inputs stay intact so the user can resume after topping up.
    LaunchedEffect(isGenerating) {
        if (isGenerating) {
            pickedFile = null
            prompt = ""
        }
    }

    // After a fresh result lands the modal stays open but the user is scrolled
    // down by the Generate button — scroll the surface back to the top anchor so
    // they immediately see the new card.
    var topAnchor by remember { mutableStateOf<org.w3c.dom.HTMLElement?>(null) }
    LaunchedEffect(newAvatarUrl) {
        val anchor = topAnchor
        if (newAvatarUrl != null && anchor != null) {
            anchor.asDynamic().scrollIntoView(js("({ behavior: 'smooth', block: 'start' })"))
        }
    }

    val isBusy = isGenerating || isConfirming

    fun handleFile(file: org.w3c.files.File) {
        validationError = null
        if (!ALLOWED_TYPES.contains(file.type)) {
            validationError = t.avatarGenErrorFileType
            return
        }
        if (file.size.toInt() > MAX_FILE_SIZE) {
            validationError = t.avatarGenErrorFileSize
            return
        }
        val reader = FileReader()
        reader.onload = {
            val arrayBuffer = reader.result.unsafeCast<org.khronos.webgl.ArrayBuffer>()
            val int8Array = org.khronos.webgl.Int8Array(buffer = arrayBuffer)
            val bytes = int8Array.unsafeCast<ByteArray>()
            val previewUrl = createObjectUrl(file)
            pickedFile = PickedFile(bytes, file.name, file.type, previewUrl)
        }
        reader.readAsArrayBuffer(file)
    }

    fun pickFile() {
        if (isBusy) return
        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        input.accept = "image/jpeg,image/png,image/webp"
        input.onchange = { _ ->
            input.files?.get(0)?.let { handleFile(it) }
        }
        input.click()
    }

    // Desktop → centred modal; mobile → bottom sheet. Same body either way; the
    // loader is passed as an overlay so it covers the whole surface (not scrolled).
    AvatarChrome(
        onDismiss = onClose,
        overlay = { GeneratingOverlay(isGenerating || isConfirming) },
    ) {
            // Scroll target pinned at the very top of the surface.
            Div(attrs = {
                ref { el ->
                    topAnchor = el
                    onDispose { topAnchor = null }
                }
            })
            Header()

            Column(
                modifier = Modifier.fillMaxWidth().sGap(32),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                CardComparison(
                    cardVisual = cardVisual,
                    sportName = sportName,
                    currentAvatarUrl = currentAvatarUrl,
                    newAvatarUrl = newAvatarUrl,
                )

                // Blue "Done" CTA — appears under the cards once an avatar has
                // been generated (it auto-saves), so the user can confirm and
                // close. Dismiss is the same handler the footer Close uses.
                if (hasNewAttempt) {
                    PrimaryPill(
                        label = t.commonDone,
                        enabled = !closeLocked,
                        height = 46,
                        paddingVertical = 14,
                        paddingHorizontal = 60,
                        onClick = onClose,
                    )
                }

                Column(modifier = Modifier.fillMaxWidth().sGap(32)) {
                    // Optional placement — exactly one of the two fields is
                    // truly optional at any given moment:
                    //  • initial (no prior attempt, no picked file) → prompt is
                    //    optional, photo is required
                    //  • there's any prior attempt (stored or in-session new)
                    //    and no new photo → photo is optional (refine with
                    //    prompt only), prompt is required
                    //  • there's a prior attempt and a new photo → back to the
                    //    initial state: prompt is optional alongside the upload
                    val canRefineAny = canRefineExisting || hasNewAttempt
                    val uploadOptional = canRefineAny && pickedFile == null
                    val promptOptional = !uploadOptional

                    DropZone(
                        picked = pickedFile,
                        isBusy = isBusy,
                        showOptional = uploadOptional,
                        onPick = { pickFile() },
                        onClear = { pickedFile = null },
                        onFileDropped = { handleFile(it) },
                    )

                    PromptSection(
                        value = prompt,
                        onChange = { next ->
                            prompt = if (next.length > MAX_PROMPT_CHARS) next.take(MAX_PROMPT_CHARS) else next
                        },
                        enabled = !isBusy,
                        showOptional = promptOptional,
                    )

                    val displayError = validationError ?: backendError
                    if (displayError != null) {
                        SpanText(
                            displayError,
                            modifier = Modifier
                                .sFontSize(13)
                                .color(AppColors.Error),
                        )
                    }

                    FooterButtons(
                        canCancel = !closeLocked,
                        // Generate stays available even when there's a pending
                        // attempt — the user can iterate on it:
                        //   • new photo picked → starts a fresh attempt
                        //     (replaces the current one)
                        //   • prompt typed (with an existing attempt to refine
                        //     against, fresh or stored) → tweaks that attempt
                        canGenerate = !isBusy && (
                            pickedFile != null ||
                                (canRefineAny && prompt.isNotBlank())
                            ),
                        generateLabel = if (isGenerating) t.avatarGenGenerating else t.avatarGenGenerate,
                        // Wallet-style charge ("ⓒ × 20") appended by the pill;
                        // hidden while generating or until pricing loads.
                        coinPrice = if (isGenerating) null else pricing?.let { formatCoins(it.avatarGeneration) },
                        onCancel = onClose,
                        // Don't clear the picked file / prompt here — the
                        // onUpload/onRefine wrappers in the parent may bail
                        // out (e.g. open the top-up dialog when the wallet is
                        // empty), and dumping the user's input on bail would
                        // force them to re-pick the photo after topping up.
                        // The reactive `LaunchedEffect(isGenerating)` below
                        // clears the inputs the moment a real request lands
                        // in flight, so successful submissions still reset.
                        onGenerate = {
                            val f = pickedFile
                            if (f != null) {
                                onUpload(f.bytes, f.name, f.type, prompt.ifBlank { null })
                            } else if (canRefineAny && prompt.isNotBlank()) {
                                // Refine works on either source — the ViewModel
                                // prefers the in-session newAttempt and falls
                                // back to the stored attempt.
                                onRefine(prompt)
                            }
                        },
                    )
                }
            }
    }
}

// =============================================================================
// Chrome — centred modal (desktop) or bottom sheet (mobile)
// =============================================================================
@Composable
private fun AvatarChrome(
    onDismiss: () -> Unit,
    overlay: @Composable () -> Unit,
    content: @Composable () -> Unit,
) {
    if (isMobile()) {
        BottomSheet(onDismiss = onDismiss) {
            Box(
                modifier = Modifier.fillMaxWidth().styleModifier { property("position", "relative") }
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sGap(24)
                        .styleModifier {
                            property("max-height", "78vh")
                            property("overflow-y", "auto")
                            property("overflow-x", "hidden")
                            property("scrollbar-width", "none")
                        },
                ) {
                    content()
                    // Trailing spacer — Chrome/Safari drop padding-bottom on a
                    // flex scroll container at scroll-end, so the footer would be
                    // unreachable. A sized spacer Div keeps it in reach.
                    Box(modifier = Modifier.fillMaxWidth().sHeight(28).styleModifier {
                        property("flex-shrink", "0")
                    })
                }
                overlay()
            }
        }
    } else {
        Box(
            modifier = Modifier
                .position(Position.Fixed)
                .top(0.px).left(0.px).right(0.px).bottom(0.px)
                .zIndex(1010)
                .styleModifier { property("background", "rgba(0, 0, 0, 0.6)") }
                .onClick { onDismiss() },
            contentAlignment = Alignment.Center,
        ) {
            Column(
                modifier = Modifier
                    .sMaxWidth(700)
                    .fillMaxWidth()
                    .sMargin(16)
                    .sBorderRadius(26)
                    .sPadding(22)
                    .sGap(24)
                    .onClick { it.stopPropagation() }
                    .styleModifier {
                        property("background", DIALOG_BG)
                        property("overflow", "hidden")
                        property("position", "relative")
                        property("max-height", "calc(100vh - ${s(32)})")
                        property("overflow-y", "auto")
                    },
            ) {
                content()
                overlay()
            }
        }
    }
}

@Composable
private fun GeneratingOverlay(isGenerating: Boolean) {
    val t = LocalAppStrings.current
    if (isGenerating) {
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("background", "rgba(9, 20, 45, 0.75)")
                property("backdrop-filter", "blur(2px)")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("z-index", "10")
            },
        ) {
            LoadingSpinner(text = t.avatarGenGenerating)
        }
    }
}

// =============================================================================
// Header — "AI Avatar" + sparkle
// =============================================================================
@Composable
private fun Header() {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.sGap(12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            t.avatarGenTitle,
            modifier = Modifier
                .sFontSize(24)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(32)
                .sLetterSpacing(0.24)
                .color(AppColors.Text.Secondary),
        )
        Icon(src = AppIcons.AiSparkle, modifier = Modifier.sSize(24))
    }
}

// =============================================================================
// Two-card comparison: current → generated preview
// =============================================================================
@Composable
private fun CardComparison(
    cardVisual: UserCardDto,
    sportName: String,
    currentAvatarUrl: String?,
    newAvatarUrl: String?,
) {
    // Large (254px) fits the wide desktop modal but is far too big for the phone
    // sheet — drop to Medium (130px) and shrink the whole comparison block (cards
    // + arrow + gap shrink together) via a local --scale so it stays compact.
    val mobile = isMobile()
    val cardSize = if (mobile) PlayerCardSize.Medium else PlayerCardSize.Large
    Row(
        modifier = Modifier.sGap(16).styleModifier {
            if (mobile) property("--scale", "calc(var(--mobile-scale) * 0.78)")
        },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center,
    ) {
        // Left — user's current avatar (or empty silhouette).
        PlayerCard(
            playerName = "",
            rating = 0,
            position = "",
            sport = sportName,
            size = cardSize,
            avatarUrl = currentAvatarUrl ?: AppIcons.EmptyAvatar,
            hideRating = true,
            hideShadow = true,
            cardAssetsBasePath = cardVisual.assetsBasePath,
            cardTextColor = cardVisual.textColor,
        )

        Icon(src = AppIcons.ArrowRight, modifier = Modifier.sSize(24))

        // Right — generated preview, or the "Generated Look" placeholder.
        // The right card is left untouched during generation; the busy state
        // is communicated by a centered loader painted over the whole dialog
        // (see `AvatarDialog`).
        Box(
            modifier = Modifier.styleModifier {
                property("position", "relative")
                if (newAvatarUrl != null) {
                    property("filter", "drop-shadow(0 0 10px rgba(0, 97, 255, 0.4))")
                }
            },
            contentAlignment = Alignment.Center,
        ) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = sportName,
                size = cardSize,
                avatarUrl = newAvatarUrl,
                hideRating = true,
                hideShadow = true,
                cardAssetsBasePath = cardVisual.assetsBasePath,
                cardTextColor = cardVisual.textColor,
            )
            if (newAvatarUrl == null) {
                GeneratedLookPlaceholder()
            }
        }
    }
}

@Composable
private fun GeneratedLookPlaceholder() {
    // Centered placeholder shown over the empty card frame until the AI
    // returns an attempt: sparkle icon stacked above the "Generated Look"
    // label so the glyph sits on top of "Generated" line.
    Column(
        modifier = Modifier
            .sGap(8)
            .styleModifier {
                property("position", "absolute")
                property("top", "50%")
                property("left", "50%")
                property("transform", "translate(-50%, -50%)")
                property("pointer-events", "none")
            },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Icon(src = AppIcons.AiSparkle, modifier = Modifier.sSize(40))
        SpanText(
            LocalAppStrings.current.avatarGenGeneratedLook,
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(22)
                .sLetterSpacing(0.09)
                .color(AppColors.Text.Secondary)
                .styleModifier { property("text-align", "center") },
        )
    }
}

// =============================================================================
// Drop zone — dashed border, click to pick, native drag-and-drop
// =============================================================================
@Composable
private fun DropZone(
    picked: PickedFile?,
    isBusy: Boolean,
    showOptional: Boolean,
    onPick: () -> Unit,
    onClear: () -> Unit,
    onFileDropped: (org.w3c.files.File) -> Unit,
) {
    Div(attrs = {
        style {
            property("width", "100%")
            property("height", s(165))
            property("border-radius", s(22))
            property("border", "2px dashed $DROPZONE_BORDER")
            property("background", DROPZONE_BG)
            property("box-sizing", "border-box")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("cursor", if (isBusy) "default" else "pointer")
            property("position", "relative")
            property("overflow", "hidden")
            if (isBusy) property("opacity", "0.6")
        }
        if (!isBusy) onClick { onPick() }
        // Native HTML5 drag-and-drop. preventDefault on dragover is what
        // unlocks the drop event in the browser.
        onDragOver { it.preventDefault() }
        onDragEnter { it.preventDefault() }
        onDrop { e ->
            e.preventDefault()
            if (isBusy) return@onDrop
            val file = e.dataTransfer?.files?.get(0)
            if (file != null) onFileDropped(file)
        }
    }) {
        if (picked == null) {
            DropZoneEmptyState(showOptional = showOptional)
        } else {
            DropZonePreview(picked = picked, onClear = onClear, isBusy = isBusy)
        }
    }
}

@Composable
private fun DropZoneEmptyState(showOptional: Boolean) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.sGap(10),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Column(
            modifier = Modifier.sGap(16),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            // 48×48 round + button — same gradient-mask ring as the Add Photo
            // button in the Create Post popup so the affordance stays
            // consistent across upload zones.
            Box(
                modifier = Modifier
                    .sSize(48)
                    .sBorderRadius(100)
                    .styleModifier {
                        property("position", "relative")
                        property(
                            "background",
                            AppGradients.InteractiveSecondary,
                        )
                    },
                contentAlignment = Alignment.Center,
            ) {
                GradientBorderRing(radiusPx = 100)
                Icon(src = AppIcons.AddSecondary, modifier = Modifier.sSize(20))
            }
            Row(
                modifier = Modifier.sGap(6),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    t.avatarGenDropPhotoHere,
                    modifier = Modifier
                        .sFontSize(14)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(22)
                        .sLetterSpacing(0.028)
                        .color(AppColors.Text.Secondary),
                )
                if (showOptional) OptionalBadge()
            }
        }
        SpanText(
            t.avatarGenMaxFileSize,
            modifier = Modifier
                .sFontSize(12)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(12)
                .sLetterSpacing(0.012)
                .color(AppColors.Text.OnSurfaceMuted),
        )
    }
}

@Composable
private fun DropZonePreview(picked: PickedFile, onClear: () -> Unit, isBusy: Boolean) {
    // Thumbnail + filename + remove pill, fills the dashed zone so the user
    // can see exactly which photo will be sent.
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(16)
            .styleModifier { property("padding", "0 ${s(22)}") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .sSize(110)
                .sBorderRadius(16)
                .styleModifier {
                    property("background-image", "url(${picked.previewUrl})")
                    property("background-size", "cover")
                    property("background-position", "center")
                    property("flex-shrink", "0")
                },
        )
        Column(modifier = Modifier.flexGrow(1).sGap(4)) {
            SpanText(
                picked.name,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    },
            )
            SpanText(
                LocalAppStrings.current.avatarGenReadyToGenerate,
                modifier = Modifier
                    .sFontSize(12)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(18)
                    .color(AppColors.Text.OnSurfaceMuted),
            )
        }
        Box(
            modifier = Modifier
                .sSize(36)
                .sBorderRadius(100)
                .styleModifier {
                    property("background", INTERACTIVE_TRANSLUCENT_BG)
                    property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
                    property("flex-shrink", "0")
                    if (isBusy) property("opacity", "0.5")
                }
                .then(
                    if (isBusy) Modifier else Modifier.cursor(Cursor.Pointer).onClick {
                        it.stopPropagation()
                        onClear()
                    }
                ),
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                "×",
                modifier = Modifier
                    .sFontSize(18)
                    .color(AppColors.Text.Secondary),
            )
        }
    }
}

// =============================================================================
// Custom prompt — label + info tooltip + 300-char textarea with counter
// =============================================================================
@Composable
private fun PromptSection(
    value: String,
    onChange: (String) -> Unit,
    enabled: Boolean,
    showOptional: Boolean,
) {
    val t = LocalAppStrings.current
    var showInfo by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier.fillMaxWidth().sGap(8),
        horizontalAlignment = Alignment.End,
    ) {
        Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
            // Label row — "Custom prompt" + info toggle.
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier { property("position", "relative") },
            ) {
                Row(
                    modifier = Modifier.sGap(6),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        t.avatarGenCustomPrompt,
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(18)
                            .sLetterSpacing(0.032)
                            .color(AppColors.Text.Secondary),
                    )
                    Box(
                        modifier = Modifier
                            .sSize(24)
                            .cursor(Cursor.Pointer)
                            .onClick { showInfo = !showInfo }
                            .styleModifier {
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("opacity", if (showInfo) "1" else "0.7")
                            },
                    ) {
                        Icon(src = AppIcons.InfoCircle, modifier = Modifier.sSize(20))
                    }
                    if (showOptional) OptionalBadge()
                }
                if (showInfo) PromptInfoPopover()
            }

            // Textarea — translucent blue surface, 108px tall, 300-char cap.
            // The visible border is a gradient ring painted via mask-composite
            // on an absolutely-positioned overlay so the inner <textarea> keeps
            // its focus/selection state without fighting the border.
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(108)
                    .sBorderRadius(18)
                    .styleModifier {
                        property("position", "relative")
                        property("background", INTERACTIVE_TRANSLUCENT_BG)
                        property("box-sizing", "border-box")
                    },
            ) {
                GradientBorderRing(radiusPx = 18)
                TextArea(value = value, attrs = {
                    onInput { onChange(it.value) }
                    placeholder(t.avatarGenPromptPlaceholder)
                    maxLength(MAX_PROMPT_CHARS)
                    rows(4)
                    if (!enabled) attr("disabled", "")
                    style {
                        property("width", "100%")
                        property("height", "100%")
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("resize", "none")
                        property("color", "#bed6f5")
                        property("font-family", "Urbanist, sans-serif")
                        property("font-size", s(16))
                        property("font-weight", "300")
                        property("line-height", "1.5")
                        property("letter-spacing", s(0.032))
                        property("padding", s(12))
                        property("box-sizing", "border-box")
                    }
                })
            }
        }
        // Character counter — right-aligned per Figma.
        SpanText(
            "${value.length}/$MAX_PROMPT_CHARS",
            modifier = Modifier
                .sFontSize(10)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(12)
                .sLetterSpacing(0.1)
                .color(AppColors.Text.OnSurfaceMuted)
                .styleModifier { property("padding-right", s(2)) },
        )
    }
}

@Composable
private fun PromptInfoPopover() {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .styleModifier {
                property("position", "absolute")
                property("top", "calc(100% + ${s(6)})")
                property("left", "0")
                property("z-index", "20")
                property("min-width", "calc(280 * var(--scale, 1))")
                property("max-width", "calc(360 * var(--scale, 1))")
            }
            .sBorderRadius(12)
            .sPadding(12, 14)
            .styleModifier {
                property("background", "linear-gradient(160deg, #0F172A 0%, #1A2340 100%)")
                property("border", "1px solid rgba(41, 52, 138, 0.5)")
                property("box-shadow", "0 ${s(8)} ${s(24)} rgba(0, 0, 0, 0.4)")
            },
    ) {
        Column(modifier = Modifier.sGap(8)) {
            SpanText(
                t.avatarGenInfoDefaultBehavior,
                modifier = Modifier.sFontSize(12).color(AppColors.Text.Primary),
            )
            SpanText(
                t.avatarGenInfoPromptIntro,
                modifier = Modifier.sFontSize(12).color(AppColors.Text.Secondary),
            )
            Column(modifier = Modifier.sGap(4)) {
                InfoBullet(t.avatarGenInfoBulletZoom)
                InfoBullet(t.avatarGenInfoBulletObject)
            }
        }
    }
}

@Composable
private fun InfoBullet(text: String) {
    Row(modifier = Modifier.sGap(6), verticalAlignment = Alignment.Top) {
        SpanText(
            "•",
            modifier = Modifier.sFontSize(12).color(AppColors.Text.Muted),
        )
        SpanText(
            text,
            modifier = Modifier.sFontSize(12).color(AppColors.Text.Secondary),
        )
    }
}

// =============================================================================
// Footer — Cancel + Generate, both outlined translucent pills
// =============================================================================
@Composable
private fun FooterButtons(
    canCancel: Boolean,
    canGenerate: Boolean,
    generateLabel: String,
    coinPrice: String?,
    onCancel: () -> Unit,
    onGenerate: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().sGap(12),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        OutlinedPill(
            label = LocalAppStrings.current.commonClose,
            enabled = canCancel,
            height = 46,
            paddingVertical = 14,
            paddingHorizontal = 32,
            onClick = onCancel,
        )
        PrimaryPill(
            label = generateLabel,
            enabled = canGenerate,
            height = 46,
            paddingVertical = 14,
            paddingHorizontal = 32,
            coinPrice = coinPrice,
            onClick = onGenerate,
        )
    }
}

// Primary sport-tinted pill — used for both Apply and Generate so the two
// "go ahead" actions stay visually identical.
@Composable
private fun PrimaryPill(
    label: String,
    enabled: Boolean,
    height: Int,
    paddingVertical: Int,
    paddingHorizontal: Int,
    // Coin charge shown wallet-style after the label: coin glyph first, then
    // "× 20". Null hides the price block entirely.
    coinPrice: String? = null,
    onClick: () -> Unit,
) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .sHeight(height)
            .sBorderRadius(100)
            .styleModifier {
                property("padding", "${s(paddingVertical)} ${s(paddingHorizontal)}")
                property(
                    "background",
                    sportColors.primary.toRgb().let {
                        "rgb(${it.red}, ${it.green}, ${it.blue})"
                    },
                )
                if (!enabled) property("opacity", "0.5")
            }
            .then(if (enabled) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier),
        contentAlignment = Alignment.Center,
    ) {
        Row(modifier = Modifier.sGap(6), verticalAlignment = Alignment.CenterVertically) {
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(sportColors.onPrimary),
            )
            if (coinPrice != null) {
                CoinIcon(32)
                SpanText(
                    "× $coinPrice",
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(18)
                        .sLetterSpacing(0.032)
                        .color(sportColors.onPrimary),
                )
            }
        }
    }
}

// Outlined pill — translucent fill + shared `GradientBorderRing` (the
// mask-composite overlay used by every outlined pill in the app). The ring
// fills the whole button because the outer Box carries no padding; the
// padding lives on the inner content wrapper instead.
@Composable
private fun OutlinedPill(
    label: String,
    enabled: Boolean,
    height: Int,
    paddingVertical: Int,
    paddingHorizontal: Int,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .sHeight(height)
            .sBorderRadius(100)
            .styleModifier {
                property("position", "relative")
                property("background", INTERACTIVE_TRANSLUCENT_BG)
                if (!enabled) property("opacity", "0.5")
            }
            .then(if (enabled) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier),
        contentAlignment = Alignment.Center,
    ) {
        GradientBorderRing(radiusPx = 100)
        Box(
            modifier = Modifier.styleModifier {
                property("padding", "${s(paddingVertical)} ${s(paddingHorizontal)}")
            },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(AppColors.Text.Secondary),
            )
        }
    }
}

// Small muted "Optional" badge — sits next to whichever field is non-required
// at the current step of the avatar flow (upload vs. prompt).
@Composable
private fun OptionalBadge() {
    SpanText(
        LocalAppStrings.current.avatarGenOptionalBadge,
        modifier = Modifier
            .sFontSize(12)
            .fontWeight(AppTypography.Regular)
            .sLineHeight(18)
            .sLetterSpacing(0.024)
            .color(AppColors.Text.OnSurfaceMuted),
    )
}

private fun createObjectUrl(file: org.w3c.files.File): String {
    return js("URL.createObjectURL(file)") as String
}
